import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type {
  OutreachMetrics,
  TeamMetrics,
  ConversionFunnel,
  QueueResponse,
  OutreachLeadSummary,
  OutreachEvent,
  OutreachCadence,
  OutreachConfig,
  SuggestedMessage,
  CsvImportRecord,
} from '@/types/outreach'

// ── Metrics ──

export function useOutreachMetrics(days = 30) {
  return useQuery({
    queryKey: ['outreach-metrics', days],
    queryFn: async () => {
      const { data } = await api.get<OutreachMetrics>('/v1/outreach/metrics', { params: { days } })
      return data
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useTeamMetrics(days = 30) {
  return useQuery({
    queryKey: ['outreach-team-metrics', days],
    queryFn: async () => {
      const { data } = await api.get<TeamMetrics>('/v1/outreach/metrics/team', { params: { days } })
      return data
    },
    staleTime: 2 * 60 * 1000,
    retry: 1,
  })
}

export function useConversionFunnel() {
  return useQuery({
    queryKey: ['outreach-funnel'],
    queryFn: async () => {
      const { data } = await api.get<ConversionFunnel>('/v1/outreach/metrics/conversion-funnel')
      return data
    },
    staleTime: 2 * 60 * 1000,
  })
}

// ── Queue ──

export function useOutreachQueue(params: {
  search?: string
  outreach_status?: string
  page?: number
  per_page?: number
} = {}) {
  const { page = 1, per_page = 20, ...rest } = params
  return useQuery({
    queryKey: ['outreach-queue', params],
    queryFn: async () => {
      const { data } = await api.get<QueueResponse>('/v1/outreach/queue', {
        params: { page, per_page, ...rest },
      })
      return data
    },
    staleTime: 30 * 1000,
    keepPreviousData: true,
  })
}

// ── Lead Outreach ──

export function useLeadOutreach(leadId: string) {
  return useQuery({
    queryKey: ['lead-outreach', leadId],
    queryFn: async () => {
      const { data } = await api.get<OutreachLeadSummary>(`/v1/outreach/leads/${leadId}/outreach`)
      return data
    },
    enabled: !!leadId,
    staleTime: 60 * 1000,
  })
}

export function useUpdateOutreachStatus(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (newStatus: string) => {
      const { data } = await api.put(`/v1/outreach/leads/${leadId}/outreach/status`, { status: newStatus })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-outreach', leadId] })
      queryClient.invalidateQueries({ queryKey: ['outreach-queue'] })
      queryClient.invalidateQueries({ queryKey: ['outreach-metrics'] })
    },
  })
}

export function useSuggestMessage(leadId: string) {
  return useMutation({
    mutationFn: async (context?: string) => {
      const { data } = await api.post<SuggestedMessage>(`/v1/outreach/leads/${leadId}/outreach/suggest-message`, {
        context: context || 'follow_up',
      })
      return data
    },
  })
}

// ── Events ──

export function useOutreachEvents(params?: { lead_id?: string; limit?: number }) {
  return useQuery({
    queryKey: ['outreach-events', params],
    queryFn: async () => {
      const { data } = await api.get<{ items: OutreachEvent[]; total: number }>('/v1/outreach/events', { params })
      return data
    },
    staleTime: 60 * 1000,
  })
}

export function useCreateEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (event: {
      lead_id?: string
      contact_id?: string
      event_type: string
      channel?: string
      direction?: string
      subject?: string
      body?: string
    }) => {
      const { data } = await api.post('/v1/outreach/events', event)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach-events'] })
      queryClient.invalidateQueries({ queryKey: ['outreach-queue'] })
      queryClient.invalidateQueries({ queryKey: ['outreach-metrics'] })
    },
  })
}

// ── Cadences ──

export function useOutreachCadences() {
  return useQuery({
    queryKey: ['outreach-cadences'],
    queryFn: async () => {
      const { data } = await api.get<{ items: OutreachCadence[]; total: number }>('/v1/outreach/cadences')
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useAssignCadence(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (cadenceId: string) => {
      const { data } = await api.post(`/v1/outreach/leads/${leadId}/assign-cadence`, { cadence_id: cadenceId })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-outreach', leadId] })
      queryClient.invalidateQueries({ queryKey: ['outreach-queue'] })
    },
  })
}

export function useRemoveCadence(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete(`/v1/outreach/leads/${leadId}/remove-cadence`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-outreach', leadId] })
      queryClient.invalidateQueries({ queryKey: ['outreach-queue'] })
    },
  })
}

export function useCreateCadence() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (cadence: {
      name: string
      description?: string
      is_global?: boolean
      steps: { step_order: number; action_type: string; delay_days: number; message_template?: string }[]
    }) => {
      const { data } = await api.post<OutreachCadence>('/v1/outreach/cadences', cadence)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach-cadences'] })
    },
  })
}

export function useDeleteCadence() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (cadenceId: string) => {
      await api.delete(`/v1/outreach/cadences/${cadenceId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach-cadences'] })
    },
  })
}

// ── Config ──

export function useOutreachConfig() {
  return useQuery({
    queryKey: ['outreach-config'],
    queryFn: async () => {
      const { data } = await api.get<OutreachConfig>('/v1/outreach/config')
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpdateLinkedInConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (config: { linkedin_email?: string; linkedin_profile_url?: string }) => {
      const { data } = await api.put('/v1/outreach/config/linkedin', config)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach-config'] })
    },
  })
}

export function useUpdateIMAPConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (config: {
      imap_host: string
      imap_port: number
      imap_username: string
      imap_password: string
    }) => {
      const { data } = await api.put('/v1/outreach/config/imap', config)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach-config'] })
    },
  })
}

export function useTestIMAPConnection() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ success: boolean; message: string }>('/v1/outreach/config/imap/test')
      return data
    },
  })
}

// ── CSV Import ──

export function useCsvImports() {
  return useQuery({
    queryKey: ['csv-imports'],
    queryFn: async () => {
      const { data } = await api.get<{ items: CsvImportRecord[]; total: number }>('/v1/outreach/csv/imports')
      return data
    },
    staleTime: 60 * 1000,
  })
}

export function useUploadCsv() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.post('/v1/outreach/csv/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['csv-imports'] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
  })
}

// ── Outreach Intelligence Hooks (Fase 1) ──

import type { GeneratedMessages, AIInstructions, AIKnowledgeItem } from '@/types/outreach'

// Generate 3 channel messages via AI
export function useGenerateMessages() {
  return useMutation({
    mutationFn: async ({ leadId, actionType, opportunityId }: { leadId: string; actionType: string; opportunityId?: string }) => {
      const { data } = await api.post<GeneratedMessages>(`/v1/outreach/leads/${leadId}/generate-messages`, {
        action_type: actionType,
        opportunity_id: opportunityId,
      })
      return data
    },
  })
}

// Mark lead as already connected on LinkedIn
export function useMarkConnected() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (leadId: string) => {
      const { data } = await api.post(`/v1/outreach/leads/${leadId}/mark-connected`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outreach-queue'] })
      queryClient.invalidateQueries({ queryKey: ['outreach-metrics'] })
    },
  })
}

// AI Instructions (global)
export function useAIInstructions() {
  return useQuery({
    queryKey: ['ai-instructions'],
    queryFn: async () => {
      const { data } = await api.get<AIInstructions>('/v1/settings/ai-instructions')
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpdateAIInstructions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (instructions: { global_instructions: string; tone: string; include_social_proof: boolean }) => {
      const { data } = await api.put('/v1/settings/ai-instructions', instructions)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-instructions'] })
    },
  })
}

// AI Knowledge Base
export function useAIKnowledge() {
  return useQuery({
    queryKey: ['ai-knowledge'],
    queryFn: async () => {
      const { data } = await api.get<{ items: AIKnowledgeItem[] }>('/v1/settings/ai-knowledge')
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useUploadKnowledge() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.post('/v1/settings/ai-knowledge/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-knowledge'] })
    },
  })
}

export function useDeleteKnowledge() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/v1/settings/ai-knowledge/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-knowledge'] })
    },
  })
}

// Partner-specific instructions (BDR)
export function usePartnerInstructions() {
  return useQuery({
    queryKey: ['ai-partner-instructions'],
    queryFn: async () => {
      const { data } = await api.get<{ text: string }>('/v1/settings/ai-instructions/partner')
      return data
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpdatePartnerInstructions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (instructions: { text: string }) => {
      const { data } = await api.put('/v1/settings/ai-instructions/partner', instructions)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-partner-instructions'] })
    },
  })
}

// ── Next Step Suggestion (computed from lead outreach summary + cadence) ──

export function useNextStepSuggestion(leadId: string | null) {
  return useQuery({
    queryKey: ['next-step', leadId],
    queryFn: async () => {
      if (!leadId) return null
      const { data } = await api.get(`/v1/outreach/leads/${leadId}/outreach`)
      const summary = data as {
        outreach_status: string
        cadence_name: string | null
        cadence_step: number
        total_steps: number
        connection_status: string | null
        recent_events: Array<{ event_type: string; created_at: string }>
      }

      const status = summary.outreach_status || 'not_contacted'
      const isConnected = status === 'connected' || status === 'msg_sent' || status === 'replied' || status === 'follow_up' || status === 'qualified'

      // Determine suggestion
      const suggestions: Record<string, { action: string | null; label: string; needs_text: boolean }> = {
        not_contacted: { action: 'invite', label: 'Enviar convite de conexao no LinkedIn', needs_text: true },
        invite_sent: { action: null, label: 'Aguardar aceite do convite', needs_text: false },
        connected: { action: 'message', label: 'Enviar primeira mensagem de apresentacao', needs_text: true },
        msg_sent: { action: null, label: 'Aguardar resposta do contato', needs_text: false },
        replied: { action: 'qualified', label: 'Qualificar lead (BANT)', needs_text: false },
        follow_up: { action: 'follow_up', label: 'Enviar follow-up', needs_text: true },
        qualified: { action: null, label: 'Criar oportunidade no pipeline', needs_text: false },
      }

      return {
        outreach_status: status,
        suggestion: suggestions[status] || { action: null, label: 'Verificar status', needs_text: false },
        is_connected: isConnected,
        has_cadence: !!summary.cadence_name,
        cadence_name: summary.cadence_name,
        cadence_step: summary.cadence_step,
        total_steps: summary.total_steps,
      }
    },
    enabled: !!leadId,
    staleTime: 30 * 1000,
  })
}

// ── Contact Timeline (events for a lead) ──

export function useContactTimeline(leadId: string | null) {
  return useQuery({
    queryKey: ['contact-timeline', leadId],
    queryFn: async () => {
      if (!leadId) return { items: [], total: 0 }
      const { data } = await api.get<{ items: Array<{
        id: string
        event_type: string
        channel: string
        direction: string
        subject: string | null
        body: string | null
        created_at: string
        metadata: Record<string, unknown> | null
        contact_name: string | null
      }>; total: number }>('/v1/outreach/events', { params: { lead_id: leadId, limit: 50 } })
      return data
    },
    enabled: !!leadId,
    staleTime: 30 * 1000,
  })
}


// ── Outreach Contacts Grid ──

export interface OutreachContactItem {
  contact_id: string
  lead_id: string
  first_name: string | null
  last_name: string | null
  full_name: string | null
  email: string | null
  phone: string | null
  whatsapp: string | null
  linkedin_url: string | null
  job_title: string | null
  department: string | null
  is_primary: boolean
  linkedin_connection_status: string | null
  company_name: string
  company_cnpj: string | null
  lead_title: string | null
  lead_status: string | null
  outreach_status: string
  cadence_name: string | null
  cadence_step: number
  next_followup_at: string | null
  owner_name: string | null
  is_owner: boolean
  last_contacted_at: string | null
}

export interface OutreachContactsResponse {
  items: OutreachContactItem[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

export function useOutreachContactsGrid(params: {
  search?: string
  outreach_status?: string
  lead_status?: string
  page?: number
  per_page?: number
}) {
  return useQuery({
    queryKey: ['outreach-contacts-grid', params],
    queryFn: async () => {
      const { data } = await api.get<OutreachContactsResponse>('/v1/outreach/contacts-grid', { params })
      return data
    },
    staleTime: 30 * 1000,
    keepPreviousData: true,
  })
}
